import * as React from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { JobRequestList } from "components/jobRequests/JobRequestList/JobRequestList";
import { JobRequestOrigination, MachineTranslationService } from "gen/clients/llts";
import { useDialogState } from "hooks/useDialogState/useDialogState";
import { useNavigation } from "hooks/useNavigation";
import { ROUTES } from "utils/navigationUtils";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { AiToolboxDetailsPage } from "./AiToolboxDetailsPage/AiToolboxDetailsPage";
import { CreateAiToolboxPanel } from "./CreateAiToolboxPanel/CreateAiToolboxPanel";

const AiToolboxTab: React.FC = () => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const { jobRequestId } = useParams<{ jobRequestId?: string }>();
  const [isCreatePanelOpen, openCreatePanel, closeCreatePanel] = useDialogState();
  const listRefetchRef = React.useRef<() => void>();

  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  const onReturnFromDetailsClick = React.useCallback(
    () => navigateTo(ROUTES.MACHINE_TRANSLATION_AI_TOOLBOX),
    [navigateTo]
  );

  const onCreateSuccess = React.useCallback(() => {
    closeCreatePanel();
    if (listRefetchRef.current) {
      listRefetchRef.current();
    }
  }, [closeCreatePanel]);

  if (areMtSettingsLoading) {
    return <LoadingPage />;
  }

  if (mtSettingsError) {
    return <ErrorPage apiError={mtSettingsError} />;
  }

  if (!mtSettings?.enableAiToolbox) {
    return (
      <Alert sx={{ m: 3 }} color="error">
        {t("machineTranslation.aiToolbox.accessRestricted")}
      </Alert>
    );
  }

  if (jobRequestId) {
    return <AiToolboxDetailsPage jobRequestId={jobRequestId} onReturnClick={onReturnFromDetailsClick} />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Stack spacing={4}>
        <Stack spacing={2}>
          {!mtSettings?.aiToolboxSettings?.hideCreateTaskButton && (
            <Box>
              <Button variant="contained" size="large" onClick={openCreatePanel}>
                {t("machineTranslation.aiToolbox.createBtn")}
              </Button>
            </Box>
          )}
          <JobRequestList
            cacheId="aipeJobRequestList"
            origination={JobRequestOrigination.AITBX}
            refetchRef={listRefetchRef}
            getDetailsPagePath={jobRequestId => `machineTranslation/aiToolbox/${jobRequestId}`}
            showRequestTypeColumn={true}
          />
        </Stack>
      </Stack>

      {isCreatePanelOpen && (
        <CreateAiToolboxPanel mtSettings={mtSettings} onClose={closeCreatePanel} onSuccess={onCreateSuccess} />
      )}
    </Container>
  );
};

export { AiToolboxTab };
